import clsx from 'clsx';
import React from 'react';
import { Icon } from '../Icon';
import type {
  AnchorElementProps,
  ButtonElementProps,
} from '../Button/unstable/Button.types';

import type { TextLinkProps } from './TextLink.types';

/**
 * A polymorphic component that can be either an `a` or a `button` element based
 * on whether or not the `href` prop has been assigned.
 */
export const TextLink = React.forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  TextLinkProps
>(
  (
    {
      children,
      className,
      stretched = false,
      icon,
      alignIcon = 'end',
      theme,
      testId,
      ...rest
    },
    ref
  ) => {
    /**
     * Generates the markup for the Icon component
     */
    const iconContent = icon ? (
      <Icon icon={icon} className='hg-icon-link__icon' />
    ) : null;

    const commonProps = {
      className: clsx(
        {
          'stretched-link': stretched,
          'hg-icon-link': icon,
        },
        className
      ),
      ['data-theme']: theme,
      ['data-testid']: testId,
    };

    /**
     * Common content for both anchor and button elements
     */
    const commonContent = (
      <>
        {icon && alignIcon === 'start' && iconContent}
        {children}
        {icon && alignIcon === 'end' && iconContent}
      </>
    );

    if (!rest.href) {
      return (
        <button
          ref={ref as React.ForwardedRef<HTMLButtonElement>}
          type='button'
          {...commonProps}
          {...(rest as ButtonElementProps)}
        >
          {commonContent}
        </button>
      );
    }

    return (
      <a
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        {...commonProps}
        {...(rest as AnchorElementProps)}
      >
        {commonContent}
      </a>
    );
  }
);
